<template>
  <div>
    <AddOrEditTaskModal
      v-model:visible="showTaskModal"
      :message-id="taskMessageId"
      :groupId="taskGroupId"
      :initialData="taskInitialData"
      :messageContext="taskMessageContext"
      @task-created="showTaskModal = false"
    />

    <CustomModal
      title="Delete Message"
      v-model:visible="toShowDeleteMessageModal"
      width="30vw"
    >
      <span class="text-base text_color leading-5">
        Are you sure you want to delete this message?
      </span>

      <template #footer>
        <a-button
          class="primary_medium"
          :loading="performingAction"
          @click="toDeleteMessage"
        >
          Delete
        </a-button>
      </template>
    </CustomModal>

    <ForwardMessageModal
      v-model:visible="showForwardModal"
      :message-content="forwardMessageContent"
      :message-id="forwardMessageId"
      :message-sender-id="forwardMessageSenderId"
      :original-attachments="forwardMessageAttachments"
      @forwarded="handleForwardComplete"
    />

    <ChatToastContainer />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from "vue";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";
import eventBus from "@/src/utils/eventBus.js";
import ChatService from "@/src/api/ChatService.js";
import store from "@/src/store/index.js";
import CustomModal from "@/src/components/CustomModal.vue";
import AddOrEditTaskModal from "@/src/components/task_components/AddOrEditTaskModal.vue";
import ChatToastContainer from "@/src/components/projects_components/chats/wrappers/ChatToastContainer.vue";
import ForwardMessageModal from "@/src/components/projects_components/chats/wrappers/ForwardMessageModal.vue";

const router = useRouter();

const showTaskModal = ref(false);
const toShowDeleteMessageModal = ref(false);
const performingAction = ref(false);
const taskMessageId = ref(null);
const taskGroupId = ref(null);
const taskInitialData = ref(null);
const taskMessageContext = ref(null);
const deleteMessageDetails = ref(null);
const showForwardModal = ref(false);
const forwardMessageContent = ref("");
const forwardMessageId = ref(null);
const forwardMessageSenderId = ref(null);
const forwardMessageAttachments = ref([]);

const handleCreateTask = (data) => {
  try {
    // Only handle events from the main chat panel, not embedded chats
    const source = typeof data === "object" ? data.source : null;
    if (source === "embedded_chat" || source === "embedded_thread") return;

    const messageId = typeof data === "object" ? data.messageId : data;

    taskMessageId.value = messageId;
    taskGroupId.value = store.getters["chats/activeChat"]?.id || null;

    if (typeof data === "object") {
      taskInitialData.value = {};

      const allMessages = [
        ...(store.getters["chats/messages"] || []),
        ...(store.getters["chats/threadMessages"] || []),
      ];
      const msg = allMessages.find(m => String(m.id) === String(messageId));
      if (msg) {
        taskMessageContext.value = {
          text: msg.text || "",
          sender: msg.sender || null,
          sentAt: msg.sent_at || null,
        };
      } else {
        taskMessageContext.value = {
          text: data.messageContent || "",
          sender: null,
          sentAt: null,
        };
      }
    } else {
      taskInitialData.value = null;
      taskMessageContext.value = null;
    }

    showTaskModal.value = true;
  } catch (error) {
    console.error("An error occurred while creating task: ", error);
  }
};

const handleDeleteMessage = (messageDetails) => {
  try {
    deleteMessageDetails.value = messageDetails;
    toShowDeleteMessageModal.value = true;
  } catch (error) {
    console.error("An error occurred while deleting message: ", error);
  }
};

const toDeleteMessage = async () => {
  try {
    if (!deleteMessageDetails.value) {
      message.error("Invalid message details");
      return;
    }

    performingAction.value = true;

    const { messageId, recipientType, recipientId, threadId } =
      deleteMessageDetails.value;

    const response = await ChatService.deleteMessage(
      messageId,
      recipientType,
      recipientId,
      threadId,
    );

    if (response?.success) {
      toShowDeleteMessageModal.value = false;
      deleteMessageDetails.value = null;
      eventBus.emit("message-deleted", messageId);
    } else {
      message.error(response?.error || "Failed to delete message!");
    }
  } catch (error) {
    console.error("An error occurred while deleting the message: ", error);
    message.error("An error occurred while deleting the message!");
  } finally {
    performingAction.value = false;
  }
};

watch(showTaskModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false && taskMessageId.value) {
    eventBus.emit("task-modal-closed", taskMessageId.value);
    taskMessageId.value = null;
    taskGroupId.value = null;
    taskInitialData.value = null;
    taskMessageContext.value = null;
  }
});

watch(toShowDeleteMessageModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false && deleteMessageDetails.value) {
    eventBus.emit("delete-modal-closed", deleteMessageDetails.value.messageId);
  }
});

const handleForwardMessage = (data) => {
  try {
    forwardMessageId.value = data.messageId;
    forwardMessageContent.value = data.messageContent;
    forwardMessageSenderId.value = data.messageSenderId || null;
    forwardMessageAttachments.value = data.messageAttachments || [];
    showForwardModal.value = true;
  } catch (error) {
    console.error("An error occurred while opening forward modal: ", error);
  }
};

const handleForwardComplete = (chat) => {
  showForwardModal.value = false;

  if (forwardMessageId.value) {
    eventBus.emit("forward-modal-closed", forwardMessageId.value);
  }

  forwardMessageId.value = null;
  forwardMessageContent.value = "";

  if (chat) {
    store.commit("chats/SET_LAST_FETCHED_CHAT_ID", null);
    store.dispatch("chats/setActiveChat", chat);
    router.push({ name: "chats" }).catch(() => {});
  }
};

watch(showForwardModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    if (forwardMessageId.value) {
      eventBus.emit("forward-modal-closed", forwardMessageId.value);
      forwardMessageId.value = null;
      forwardMessageContent.value = "";
    }
  }
});

onMounted(() => {
  eventBus.on("create-task", handleCreateTask);
  eventBus.on("delete-message", handleDeleteMessage);
  eventBus.on("forward-message", handleForwardMessage);
});

onUnmounted(() => {
  eventBus.off("create-task", handleCreateTask);
  eventBus.off("delete-message", handleDeleteMessage);
  eventBus.off("forward-message", handleForwardMessage);
});
</script>
